import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Typography, Space, Tag, Table, Button, Statistic, Divider, Select } from 'antd';
import { LeftOutlined, ExclamationCircleFilled, ClockCircleOutlined, ShopOutlined, RiseOutlined, InfoCircleOutlined, FilterOutlined, CalendarOutlined, SoundOutlined, TagOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface OrderData {
  date: string;
  newOrders: number;
  bookedRooms: number;
  remainingRooms: number;
  currentPrice: number;
  selloutRisk: 'high' | 'medium' | 'low';
}

interface HourlyOrderData {
  hour: string;
  orders: number;
  competitor1Orders: number;
  competitor2Orders: number;
  competitor3Orders: number;
}

interface ChannelData {
  name: string;
  value: number;
  color: string;
}

interface PriceTrendData {
  date: string;
  ourPrice: number;
  competitor1Price: number; 
  competitor2Price: number;
  competitor3Price: number;
}

interface RoomType {
  id: string;
  name: string;
}

interface EventItem {
  id: string;
  date: string;
  name: string;
  type: '节假日' | '展会' | '赛事' | '促销' | '其他';
  impact: 'high' | 'medium' | 'low';
  description: string;
  startDate?: string;  // 添加开始日期
  endDate?: string;    // 添加结束日期
}

interface AlertData {
  id: string;
  title: string;
  content: string;
  time: string;
  store: string;
  orders: OrderData[];
  severity: 'high' | 'medium' | 'low';
  hourlyOrders: HourlyOrderData[];
  channelDistribution: ChannelData[];
  priceTrend: PriceTrendData[];
  roomTypes: RoomType[];
  checkInDates: string[];
  events: EventItem[];
}

const OrderSpeedChangeAlert: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // 模拟数据
  const [alertData] = useState<AlertData>({
    id: id || '1',
    title: '订单速度异常提醒',
    content: '【2025-07-20 06:00-07:00】进单16单，进单量大于订单量的50%',
    time: '2023-07-15 09:30',
    store: '秋果酒店-西湖店',
    severity: 'medium', // 添加严重程度字段
    orders: [
      { 
        date: '2023-07-20', 
        newOrders: 12, 
        bookedRooms: 35, 
        remainingRooms: 15, 
        currentPrice: 368, 
        selloutRisk: 'high' 
      },
      { 
        date: '2023-07-21', 
        newOrders: 8, 
        bookedRooms: 28, 
        remainingRooms: 22, 
        currentPrice: 358, 
        selloutRisk: 'medium' 
      },
      { 
        date: '2023-07-22', 
        newOrders: 15, 
        bookedRooms: 42, 
        remainingRooms: 8, 
        currentPrice: 398, 
        selloutRisk: 'high' 
      },
      { 
        date: '2023-07-23', 
        newOrders: 6, 
        bookedRooms: 25, 
        remainingRooms: 25, 
        currentPrice: 338, 
        selloutRisk: 'low' 
      },
      { 
        date: '2023-07-24', 
        newOrders: 10, 
        bookedRooms: 32, 
        remainingRooms: 18, 
        currentPrice: 378, 
        selloutRisk: 'medium' 
      },
      { 
        date: '2023-07-25', 
        newOrders: 18, 
        bookedRooms: 45, 
        remainingRooms: 5, 
        currentPrice: 428, 
        selloutRisk: 'high' 
      },
    ],
    hourlyOrders: [
      { hour: '06:00', orders: 3, competitor1Orders: 2, competitor2Orders: 1, competitor3Orders: 3 },
      { hour: '06:10', orders: 2, competitor1Orders: 1, competitor2Orders: 2, competitor3Orders: 2 },
      { hour: '06:20', orders: 4, competitor1Orders: 2, competitor2Orders: 3, competitor3Orders: 1 },
      { hour: '06:30', orders: 2, competitor1Orders: 3, competitor2Orders: 2, competitor3Orders: 2 },
      { hour: '06:40', orders: 3, competitor1Orders: 2, competitor2Orders: 1, competitor3Orders: 3 },
      { hour: '06:50', orders: 2, competitor1Orders: 1, competitor2Orders: 2, competitor3Orders: 2 },
    ],
    channelDistribution: [
      { name: '散客', value: 45, color: '#1890ff' },
      { name: '会员', value: 25, color: '#52c41a' },
      { name: '协议单位', value: 18, color: '#faad14' },
      { name: 'OTA', value: 12, color: '#722ed1' }
    ],
    priceTrend: [
      { date: '07-19', ourPrice: 358, competitor1Price: 349, competitor2Price: 369, competitor3Price: 339 },
      { date: '07-20', ourPrice: 368, competitor1Price: 359, competitor2Price: 379, competitor3Price: 349 },
      { date: '07-21', ourPrice: 358, competitor1Price: 369, competitor2Price: 389, competitor3Price: 359 },
      { date: '07-22', ourPrice: 398, competitor1Price: 389, competitor2Price: 399, competitor3Price: 369 },
      { date: '07-23', ourPrice: 338, competitor1Price: 329, competitor2Price: 359, competitor3Price: 319 },
      { date: '07-24', ourPrice: 378, competitor1Price: 369, competitor2Price: 389, competitor3Price: 349 },
      { date: '07-25', ourPrice: 428, competitor1Price: 399, competitor2Price: 419, competitor3Price: 379 },
    ],
    roomTypes: [
      { id: '1', name: '标准大床房' },
      { id: '2', name: '豪华大床房' },
      { id: '3', name: '商务套房' },
      { id: '4', name: '家庭套房' }
    ],
    checkInDates: [
      '2023-07-19', '2023-07-20', '2023-07-21', '2023-07-22', 
      '2023-07-23', '2023-07-24', '2023-07-25'
    ],
    events: [
      {
        id: '1',
        name: '西湖音乐节',
        type: '赛事',
        impact: 'high',
        description: '为期3天的音乐节活动，预计吸引游客5万人',
        date: '2023-07-20',
        startDate: '2023-07-19',
        endDate: '2023-07-21'
      },
      {
        id: '2',
        name: '竞品A特惠活动',
        type: '促销',
        impact: 'medium',
        description: '竞品酒店推出满3晚减200元活动',
        date: '2023-07-22',
        startDate: '2023-07-21',
        endDate: '2023-07-25'
      },
      {
        id: '3',
        name: '杭州数字经济峰会',
        type: '展会',
        impact: 'high',
        description: '为期2天的行业峰会，商务客流量预计增加40%',
        date: '2023-07-24',
        startDate: '2023-07-23',
        endDate: '2023-07-24'
      },
      {
        id: '4',
        name: '七夕情人节',
        type: '节假日',
        impact: 'medium',
        description: '情侣出行高峰，特色房型需求增加',
        date: '2023-07-25',
        startDate: '2023-07-25',
        endDate: '2023-07-25'
      }
    ]
  });

  // 筛选状态
  const [selectedRoomType, setSelectedRoomType] = useState<string>('1');
  const [selectedDate, setSelectedDate] = useState<string>('all');

  // 获取热力图配置
  const getHeatmapOptions = () => {
    const dates = alertData.orders.map(item => dayjs(item.date).format('MM-DD'));
    const newOrders = alertData.orders.map(item => [dates.indexOf(dayjs(item.date).format('MM-DD')), 0, item.newOrders]);
    const accumulatedOrders = alertData.orders.map(item => [dates.indexOf(dayjs(item.date).format('MM-DD')), 1, item.bookedRooms]);
    
    return {
      tooltip: {
        position: 'top',
        formatter: (params: any) => {
          const date = dates[params.data[0]];
          const value = params.data[2];
          const type = params.data[1] === 0 ? '新增订单' : '累计订单';
          return `${date}<br />${type}: ${value}`;
        },
        textStyle: {
          fontSize: 14
        }
      },
      grid: {
        top: '15%',
        left: '3%',
        right: '3%',
        bottom: '15%'
      },
      xAxis: {
        type: 'category',
        data: dates,
        splitArea: {
          show: true
        },
        axisLabel: {
          fontSize: 12
        }
      },
      yAxis: {
        type: 'category',
        data: ['新增订单', '累计订单'],
        splitArea: {
          show: true
        },
        axisLabel: {
          fontSize: 12
        }
      },
      visualMap: {
        min: 0,
        max: 50,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: '0%',
        inRange: {
          color: ['#e6f7ff', '#1890ff', '#002766']
        },
        textStyle: {
          fontSize: 12
        }
      },
      series: [{
        name: '订单热力图',
        type: 'heatmap',
        data: [...newOrders, ...accumulatedOrders],
        label: {
          show: true,
          fontSize: 12
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }]
    };
  };

  // 获取渠道分布饼图配置
  const getChannelDistributionOptions = () => {
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
        textStyle: {
          fontSize: 14
        }
      },
      legend: {
        orient: 'horizontal',
        bottom: 10,
        data: alertData.channelDistribution.map(item => item.name),
        textStyle: {
          fontSize: 12
        }
      },
      series: [
        {
          name: '订单渠道',
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 6,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            position: 'outside',
            formatter: '{b}: {d}%',
            fontSize: 12
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: true
          },
          data: alertData.channelDistribution.map(item => ({
            value: item.value,
            name: item.name,
            itemStyle: {
              color: item.color
            }
          }))
        }
      ]
    };
  };

  // 获取价格趋势图配置
  const getPriceTrendOptions = () => {
    const dates = alertData.priceTrend.map(item => item.date);
    const ourPrices = alertData.priceTrend.map(item => item.ourPrice);
    const competitor1Prices = alertData.priceTrend.map(item => item.competitor1Price);
    const competitor2Prices = alertData.priceTrend.map(item => item.competitor2Price);
    const competitor3Prices = alertData.priceTrend.map(item => item.competitor3Price);
    
    return {
      tooltip: {
        trigger: 'axis',
        textStyle: {
          fontSize: 14
        },
        formatter: function(params: any) {
          let result = params[0].name + '<br/>';
          params.forEach((param: any) => {
            result += 
              `<div style="display:flex;justify-content:space-between;min-width:150px">
                <span>${param.seriesName}:</span>
                <span style="font-weight:bold">¥${param.value}</span>
              </div>`;
          });
          return result;
        }
      },
      legend: {
        data: ['本店价格', '竞品A', '竞品B', '竞品C'],
        bottom: 0,
        textStyle: {
          fontSize: 12
        }
      },
      grid: {
        top: '15%',
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates,
        axisLabel: {
          fontSize: 12
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontSize: 12,
          formatter: '¥{value}'
        },
        name: '房价',
        nameTextStyle: {
          fontSize: 12
        },
        min: function(value: any) {
          return Math.floor(value.min * 0.95);
        }
      },
      series: [
        {
          name: '本店价格',
          type: 'line',
          data: ourPrices,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#1890ff'
          },
          lineStyle: {
            width: 3
          },
          label: {
            show: true,
            position: 'top',
            formatter: '¥{c}',
            fontSize: 12
          }
        },
        {
          name: '竞品A',
          type: 'line',
          data: competitor1Prices,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#ff7a45'
          },
          lineStyle: {
            width: 2,
            type: 'dashed'
          }
        },
        {
          name: '竞品B',
          type: 'line',
          data: competitor2Prices,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#52c41a'
          },
          lineStyle: {
            width: 2,
            type: 'dashed'
          }
        },
        {
          name: '竞品C',
          type: 'line',
          data: competitor3Prices,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#722ed1'
          },
          lineStyle: {
            width: 2,
            type: 'dashed'
          }
        }
      ]
    };
  };

  // 表格列定义
  const columns = [
    {
      title: '入住日期',
      dataIndex: 'date',
      key: 'date',
      render: (text: string) => dayjs(text).format('MM月DD日')
    },
    {
      title: '预警期间新订夜间数',
      dataIndex: 'newOrders',
      key: 'newOrders',
      render: (value: number) => <Text style={{ color: value > 10 ? '#f5222d' : 'inherit' }}>{value}</Text>
    },
    {
      title: '已订间夜',
      dataIndex: 'bookedRooms',
      key: 'bookedRooms'
    },
    {
      title: '剩余房间',
      dataIndex: 'remainingRooms',
      key: 'remainingRooms',
      render: (value: number) => <Text style={{ color: value < 10 ? '#faad14' : 'inherit' }}>{value}</Text>
    },
    {
      title: '平均房价',
      dataIndex: 'currentPrice',
      key: 'currentPrice',
      render: (value: number) => `¥${value}`
    },
    {
      title: '竞品均价',
      dataIndex: 'selloutRisk',
      key: 'selloutRisk',
      render: (value: string) => {
        let color = '';
        let text = '';
        switch (value) {
          case 'high':
            color = '#f5222d';
            text = '¥399';
            break;
          case 'medium':
            color = '#faad14';
            text = '¥359';
            break;
          case 'low':
            color = '#52c41a';
            text = '¥329';
            break;
          default:
            color = '#52c41a';
            text = '¥329';
        }
        return <Text style={{ color }}>{text}</Text>;
      }
    }
  ];

  // 获取趋势图配置
  const getTrendOptions = () => {
    const hours = alertData.hourlyOrders.map(item => item.hour);
    const orders = alertData.hourlyOrders.map(item => item.orders);
    const competitor1Orders = alertData.hourlyOrders.map(item => item.competitor1Orders);
    const competitor2Orders = alertData.hourlyOrders.map(item => item.competitor2Orders);
    const competitor3Orders = alertData.hourlyOrders.map(item => item.competitor3Orders);
    
    return {
      tooltip: {
        trigger: 'axis',
        textStyle: {
          fontSize: 14
        }
      },
      legend: {
        data: ['本店', '竞品A', '竞品B', '竞品C'],
        bottom: 0,
        textStyle: {
          fontSize: 12
        }
      },
      grid: {
        top: '10%',
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: hours,
        axisLabel: {
          fontSize: 12
        },
        nameLocation: 'middle',
        name: '预警时段: 06:00-07:00',
        nameGap: 25,
        nameTextStyle: {
          fontSize: 13,
          color: '#faad14',
          fontWeight: 'bold'
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontSize: 12
        },
        name: '订单数',
        nameTextStyle: {
          fontSize: 12
        }
      },
      series: [
        {
          name: '本店',
          type: 'line',
          data: orders,
          itemStyle: {
            color: '#1890ff'
          },
          lineStyle: {
            width: 3
          },
          symbolSize: 6,
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0, color: 'rgba(24,144,255,0.3)'
              }, {
                offset: 1, color: 'rgba(24,144,255,0.1)'
              }]
            }
          }
        },
        {
          name: '竞品A',
          type: 'line',
          data: competitor1Orders,
          itemStyle: {
            color: '#ff7a45'
          },
          lineStyle: {
            width: 2,
            type: 'dashed'
          },
          symbolSize: 5
        },
        {
          name: '竞品B',
          type: 'line',
          data: competitor2Orders,
          itemStyle: {
            color: '#52c41a'
          },
          lineStyle: {
            width: 2,
            type: 'dashed'
          },
          symbolSize: 5
        },
        {
          name: '竞品C',
          type: 'line',
          data: competitor3Orders,
          itemStyle: {
            color: '#722ed1'
          },
          lineStyle: {
            width: 2,
            type: 'dashed'
          },
          symbolSize: 5
        }
      ]
    };
  };

  // 获取严重程度对应的颜色
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return '#ff4d4f';
      case 'medium':
        return '#faad14';
      case 'low':
        return '#52c41a';
      default:
        return '#faad14';
    }
  };

  // 获取订单速度异常分析
  const getOrderSpeedAnalysis = () => {
    const allOrders = alertData.orders;
    const totalNewOrders = allOrders.reduce((sum, item) => sum + item.newOrders, 0);
    const averageNewOrders = Math.round(totalNewOrders / allOrders.length);
    const maxNewOrders = Math.max(...allOrders.map(item => item.newOrders));
    
    // 计算同比增长
    const previousAverage = Math.round(averageNewOrders * 0.7); // 模拟上期平均订单数
    const growthRate = Math.round((averageNewOrders - previousAverage) / previousAverage * 100);
    
    return {
      totalNewOrders,
      averageNewOrders,
      maxNewOrders,
      previousAverage,
      growthRate
    };
  };

  const orderAnalysis = getOrderSpeedAnalysis();

  // 获取事件类型图标和颜色
  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case '节假日':
        return { icon: <CalendarOutlined />, color: '#722ed1', text: '节假日' };
      case '展会':
        return { icon: <ShopOutlined />, color: '#13c2c2', text: '展会' };
      case '赛事':
        return { icon: <SoundOutlined />, color: '#fa8c16', text: '赛事' };
      case '促销':
        return { icon: <TagOutlined />, color: '#eb2f96', text: '促销' };
      case '其他':
        return { icon: <InfoCircleOutlined />, color: '#8c8c8c', text: '其他' };
      default:
        return { icon: <InfoCircleOutlined />, color: '#8c8c8c', text: '其他' };
    }
  };

  // 获取影响程度颜色
  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high':
        return '#f5222d';
      case 'medium':
        return '#faad14';
      case 'low':
        return '#52c41a';
      default:
        return '#8c8c8c';
    }
  };

  // 获取影响程度文本
  const getImpactText = (impact: string) => {
    switch (impact) {
      case 'high':
        return '高';
      case 'medium':
        return '中';
      case 'low':
        return '低';
      default:
        return '未知';
    }
  };

  // 检查事件是否包含预警日期
  const isEventCoveringWarningDate = (startDate: string, endDate: string, warningDate: string) => {
    if (!startDate || !endDate) return false;
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const warning = new Date(warningDate).getTime();
    return start <= warning && warning <= end;
  };

  return (
    <div style={{ 
      padding: '16px', 
      backgroundColor: '#f5f5f5', 
      minHeight: '100vh',
      maxWidth: '500px',
      margin: '0 auto'
    }}>
      {/* 顶部导航栏 */}
      <Card style={{ marginBottom: 16, borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space align="center">
            <RiseOutlined 
              style={{ 
                fontSize: 24, 
                color: getSeverityColor(alertData.severity)
              }} 
            />
            <Title level={4} style={{ margin: 0 }}>订单速度预警</Title>
          </Space>
          <Button 
            type="text" 
            icon={<LeftOutlined />} 
            onClick={() => navigate(-1)}
            style={{ padding: 0 }}
          >
            返回
          </Button>
        </div>
      </Card>

      {/* 核心提醒信息区 */}
      <Card 
        style={{ 
          marginBottom: '16px', 
          borderRadius: '8px', 
          borderLeft: `4px solid ${getSeverityColor(alertData.severity)}`,
          backgroundColor: alertData.severity === 'high' ? '#FFF1F0' : (
            alertData.severity === 'medium' ? '#FFFBE6' : '#F6FFED'
          ),
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {/* 每条数据单独一行 */}
          <div style={{ padding: '4px 0' }}>
            <Text>通知标题: <Text strong>{alertData.title}</Text></Text>
          </div>
          
          <div style={{ padding: '4px 0' }}>
            <Text>通知类型: <Tag color={getSeverityColor(alertData.severity)}>订单速度异常</Tag></Text>
          </div>
          
          <div style={{ padding: '4px 0' }}>
            <Text>通知内容: <Text type="warning" strong>{alertData.content}</Text></Text>
          </div>
          
          <div style={{ padding: '4px 0' }}>
            <Text>通知时间: {alertData.time}</Text>
          </div>
          
          <div style={{ padding: '4px 0' }}>
            <Text>门店名称: {alertData.store}</Text>
          </div>
        </Space>
      </Card>
      
      {/* 订单速度分析卡片 - 拆分出来的部分 */}
      <Card 
        style={{ 
          marginBottom: '16px', 
          borderRadius: '8px',
          backgroundColor: alertData.severity === 'high' ? '#FFF1F0' : (
            alertData.severity === 'medium' ? '#FFFBE6' : '#F6FFED'
          ),
          borderLeft: `4px solid ${getSeverityColor(alertData.severity)}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        {/* 订单速度分析 */}
        <div style={{ padding: '12px 0' }}>
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ fontSize: 16 }}>订单速度分析</Text>
          </div>
          
          <Row gutter={[16, 16]} align="middle">
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#faad14', fontSize: 28, fontWeight: 'bold' }}>
                  {orderAnalysis.averageNewOrders} 单
                </div>
                <Text type="secondary" style={{ fontSize: 14 }}>进单</Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 22 }}>
                  {orderAnalysis.maxNewOrders} 单
                </div>
                <Text type="secondary" style={{ fontSize: 14 }}>上周同期</Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 22 }}>
                  {orderAnalysis.totalNewOrders} 单
                </div>
                <Text type="secondary" style={{ fontSize: 14 }}>竞争圈平均</Text>
              </div>
            </Col>
          </Row>
        </div>
        
      </Card>

      {/* 智能建议区 */}
      <Card 
        title={
          <Space>
            <InfoCircleOutlined />
            <span>智能建议</span>
          </Space>
        }
        style={{ marginBottom: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        headStyle={{ padding: '0 12px' }}
        bodyStyle={{ padding: 12 }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Card
            style={{ 
              backgroundColor: '#E6F7FF',
              borderColor: '#91D5FF'
            }}
            bodyStyle={{ padding: 12 }}
            size="small"
          >
            <Text strong>建议上调价格以优化收益</Text>
            <div style={{ marginTop: 12, lineHeight: '1.5', color: 'rgba(0, 0, 0, 0.65)', fontSize: 13 }}>
              <div style={{ marginBottom: 4 }}>● 进单速度较上周同期增长{orderAnalysis.growthRate}%，说明市场需求强劲</div>
              <div style={{ marginBottom: 4 }}>● 7月22日、7月25日单日进单量较多，建议重点关注</div>
              <div style={{ marginBottom: 4 }}>● 建议提高这些日期的房价，预计可提升10-15%收益</div>
            </div>
            <div style={{ fontSize: 12, color: '#8C8C8C', marginTop: 4 }}>
              建议生成时间: {alertData.time}
            </div>
          </Card>
          
          <Button 
            type="primary" 
            block
            onClick={() => alert('跳转到调价页面')}
          >
            去调价
          </Button>
        </Space>
      </Card>

      {/* 入住日期分布区域 */}
      <Card 
        title={<span style={{ fontSize: '16px' }}>入住日期分布</span>} 
        style={{ marginBottom: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        bodyStyle={{ padding: '0 8px' }}
      >
        <Table 
          dataSource={alertData.orders} 
          columns={columns} 
          rowKey="date" 
          pagination={false}
          scroll={{ x: 'max-content' }}
          size="small"
          style={{ fontSize: '14px' }}
        />
      </Card>

      {/* 价格趋势图 - 新增区域 */}
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '16px' }}>入住日价格变化趋势</span>
            <Space>
              <Select
                size="small"
                style={{ width: 100 }}
                defaultValue="all"
                value={selectedDate}
                onChange={(value) => setSelectedDate(value)}
                placeholder="入住日筛选"
                options={[
                  { value: 'all', label: '全部日期' },
                  ...alertData.checkInDates.map(date => ({ 
                    value: date, 
                    label: dayjs(date).format('MM-DD') 
                  }))
                ]}
                prefix={<FilterOutlined />}
              />
              <Select
                size="small"
                style={{ width: 120 }}
                defaultValue="1"
                value={selectedRoomType}
                onChange={(value) => setSelectedRoomType(value)}
                placeholder="房型筛选"
                options={alertData.roomTypes.map(type => ({ 
                  value: type.id, 
                  label: type.name 
                }))}
                prefix={<FilterOutlined />}
              />
            </Space>
          </div>
        }
        style={{ marginBottom: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      >
        <ReactECharts 
          option={getPriceTrendOptions()} 
          style={{ height: '280px', width: '100%' }}
        />
        <div style={{ marginTop: '16px', color: '#8c8c8c', fontSize: '13px' }}>
          <Text type="secondary">* 价格趋势展示本店与竞品的价格变化对比，可判断竞品定价策略对订单速度的影响</Text>
        </div>
      </Card>

      {/* 趋势图区域 - 现在放在入住日期分布下方 */}
      <Card 
        title={<span style={{ fontSize: '16px' }}>预警期间进单趋势</span>} 
        style={{ marginBottom: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      >
        <ReactECharts 
          option={getTrendOptions()} 
          style={{ height: '250px', width: '100%' }}
        />
        <div style={{ marginTop: '16px', color: '#8c8c8c', fontSize: '13px' }}>
          <Text type="secondary">* 趋势图展示预警时段内本店与三家主要竞品的进单数量对比，数据截止2025-07-20 06:00-07:00</Text>
        </div>
      </Card>

      {/* 渠道分布区域 - 替换热力图 */}
      <Card 
        title={<span style={{ fontSize: '16px' }}>订单渠道分布</span>} 
        style={{ marginBottom: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      >
        <ReactECharts 
          option={getChannelDistributionOptions()} 
          style={{ height: '280px', width: '100%' }}
        />
        <div style={{ marginTop: '16px', color: '#8c8c8c', fontSize: '13px' }}>
          <Text type="secondary">* 渠道分布展示预警期间各渠道的订单占比情况，散客渠道占比最高</Text>
        </div>
      </Card>

      {/* 大事件信息板块 */}
      <Card 
        title={
          <Space>
            <CalendarOutlined />
            <span>大事件信息</span>
          </Space>
        }
        style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        headStyle={{ padding: '0 12px' }}
        bodyStyle={{ padding: 12 }}
      >
        {alertData.events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Text type="secondary">暂无相关大事件信息</Text>
          </div>
        ) : (
          <>
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {alertData.events.map(event => {
                // 根据事件类型获取颜色
                const typeColor = getEventTypeIcon(event.type).color;
                const impactColor = getImpactColor(event.impact);
                const impactText = getImpactText(event.impact);
                
                return (
                  <div 
                    key={event.id} 
                    style={{ 
                      borderLeft: `4px solid ${typeColor}`,
                      padding: '12px 16px',
                      backgroundColor: '#f9f9f9',
                      borderRadius: '4px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <Space>
                        <Text strong style={{ fontSize: 16 }}>{event.name}</Text>
                        <Tag color={typeColor}>
                          {getEventTypeIcon(event.type).text}
                        </Tag>
                      </Space>
                      <Tag color={impactColor}>
                        影响度: {impactText}
                      </Tag>
                    </div>
                    
                    <div style={{ marginBottom: 8, color: 'rgba(0, 0, 0, 0.45)' }}>
                      日期: {dayjs(event.startDate || event.date).format('YYYY-MM-DD')}
                      {event.startDate !== event.endDate && event.endDate ? 
                        ` 至 ${dayjs(event.endDate).format('YYYY-MM-DD')}` : ''}
                    </div>
                    
                    <div style={{ marginBottom: 0, fontSize: '14px' }}>
                      {event.description || `${event.name}期间客流量可能${impactText === '高' ? '大幅' : impactText === '中' ? '中度' : '略微'}增加，建议关注房价和库存情况`}
                    </div>
                  </div>
                );
              })}
            </Space>
            
            <div style={{ marginTop: 16, color: '#8c8c8c', fontSize: 13 }}>
              * 事件可能对订单量和房价产生显著影响，建议结合事件进行分析和决策
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default OrderSpeedChangeAlert; 